import React, { ReactElement, ReactNode } from 'react'
import { Subscription } from 'rxjs'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { ReportTypes } from '../../../NonReact/constants/ReportTypes'
import { Validate, ValidationRule, ValidationValueMessage } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { SelectOptions } from '../Components/Selects/Types'
import { PasswordInputConfig } from '../Types'
import { CallIntervalTypes } from '../../ConfigureReport/ReportTab/Constants'
import { IconName } from '../Components/Icon'
import { IconSize } from '../../../NonReact/constants/React/Icon/Constants'
import { HeadingLevel } from '../Components/Heading'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { IntegrationType } from '../../TelSysConfiguration/Constants/Constants'
import { TimingOptions } from '../../../NonReact/common/TimingOptions'
import {
    APIListData,
    CallDetailsInfo,
    JSONResponse,
    ServerError,
    StandardRESTOptions
} from '../../../NonReact/common/Types/Types'
import { AgentResponse, DeviceResponse } from '../../../NonReact/Types/Types'

/**
 * General error properties type.
 */
export type GeneralErrorGeneric = {
    ErrorCode: number
    ErrorMessage: string
}

/**
 * Validation error properties type.
 */
export type ValidationErrorGeneric = {
    ErrorCode: number
    ErrorMessage: string
}

/**
 * Email error properties type.
 */
export type EmailErrorGeneric = {
    UserID: string
    ErrorCode: number
    ErrorMessage: string
}

/**
 * Validation error for CSV imports properties type.
 */
export type ValidationErrorCSV = {
    ErrorCode: number
    ErrorMessage: string
    ID: string | null
    LineNumber: number
    HasPlaceholder: boolean
}

/**
 * User properties type.
 */
export type User = {
    UserID: string
    EmailAddress: string
    FullName: string
    LockOut: boolean
    PasswordChangeRequired: boolean
    PermissionsReportingAccess: string
    PermissionsUserMobileAppAdvanced: boolean
    PermissionsUserSystemAdministrator: boolean
    PermissionsAdminServiceNotifications: boolean
    PermissionsAdminConcurrentSignInsAllowed: boolean
    PermissionsIsOmniChannelACDAgent: boolean
    PermissionsTelSysID: number
    PermissionsPartitionID: string
    PermissionsDeviceID: string
    PermissionsACDAgentID: string
    EndpointTelSysID: number
    EndpointPartitionID: string
    EndpointDeviceID: string
    EndpointACDAgentID: string
    DisableBrowserMediaPlugin: boolean
    DisableDragAndDropFeatures: boolean
    JavaScriptDebugOutput: boolean
}

export type RptFilterEntityInfo = {
    EntityInfo: {
        ReportCapabilityInfo: {
            StatFlags: string
            CallCtrlCapabilityFlags: string
            IsMeasuredACDNotAvailableReasonTextIDs: string // 'true',
            FilterDevicesIfGrpIncACDItemsForGrpDevOnlyDefault: string // 'false'
        }
        TelSvrs: TelephoneServerEntityItem
        Partitions: GenericFilterEntityItem
        ACDAgents: GenericFilterEntityItem
        Devices: GenericFilterEntityItem
        DevicesRouting: GenericFilterEntityItem
        DevicesGroupsACD: GenericFilterEntityItem
        CodesAccount: GenericFilterEntityItem
        CodesACDNotAvailable?: GenericFilterEntityItem
    }
}

export type GenericFilterEntityDataItems = {
    ID: string
    Description: string
    EncodedID?: string
    PartIDOnTelSys?: string
    Type?: number
}

export type GenericFilterEntityItem = {
    Data: GenericFilterEntityDataItems[]
}

export type TelephoneServerEntityItem = {
    Data: TelephoneServerEntityDataItem[]
}

export type TelephoneServerEntityDataItem = {
    ID: string
    Description: string
    Type?: number
    HistoricOnly: boolean
}

export type RptConfigurationDataAddDto = RptConfigurationDataRequestDto

export type RptConfigurationDataUpdateDto = RptConfigurationDataRequestDto & {
    ID: string
}

export type RptConfigurationDataRequestDto = {
    // IsDescriptionChanged: boolean
    // IsSetCallsInAnsThresChanged: boolean
    Description: string
    CustomIntervalValue?: number
    CustomIntervalType?: CallIntervalTypes
    FilterEntityType?: number
    Information: string
    Type: number
    // StatisticsContent: string
    StatisticsDisplay: string
    StatisticsSettings: string
    // MethodsGroupedBy: string
    // DateTimeBinned: string
    // StatisticsOrderedBy: string
    // StatisticsOrderedByAscOrDesc: string
    Duration: number
    // DurationIsUTC: boolean
    DurationCustomStart: string
    DurationCustomEnd: string
    DurationTimeOfDayStart: string
    DurationTimeOfDayEnd: string
    DurationDaysOfWeek: number
    DurationDayCount?: number
    // FilterIDDb: string
    // FilterIDTimeSync: string
    FilterCallIDThread: string
    // FilterCallIDThreadWCMatches: boolean
    FilterCallIDTelSys: string
    // FilterCallIDTelSysWCMatches: boolean
    FilterTelephoneSystemID: number
    FilterPartitionIDs: string
    FilterGroupIDs?: string
    FilterTelNos: string
    FilterTelNoDescriptions: string
    FilterDevices: string
    FilterDeviceTypes: string
    FilterDevicesIfGrpIncGrpMmbrCalls: boolean
    FilterDevicesIfGrpIncACDItemsForGrpDevOnly: boolean
    FilterACDAgents: string
    FilterLocation: number
    FilterCallType: number
    FilterCallTypeConfCallAsExternal: boolean
    FilterCallChannels: string
    FilterSubject: string
    FilterAnsType: number
    FilterCLIType: number
    FilterDDIDigits: string
    FilterAccountCodes: string
    FilterMovedToTelNos: string
    FilterMovedFromDevices: string
    FilterMovedFromACDAgents: string
    FilterMovedSegNo: number
    FilterMovedSegReasonStarted: number
    FilterMovedSegReasonEnded: number
    FilterCallTimeRngAllCmp: number
    FilterCallTimeRngAllValue1Text: string
    FilterCallTimeRngAllValue2Text: string
    FilterCallTimeRngSegCmp: number
    FilterCallTimeRngSegValue1Text: string
    FilterCallTimeRngSegValue2Text: string
    FilterCallTimeTalkCmp: number
    FilterCallTimeTalkValue1Text: string
    FilterCallTimeTalkValue2Text: string
    FilterCallTimeAllCmp: number
    FilterCallTimeAllValue1Text: string
    FilterCallTimeAllValue2Text: string
    FilterCallTimeHeldCmp: number
    FilterCallTimeHeldValue1Text: string
    FilterCallTimeHeldValue2Text: string

    FilterUnreturnedLostCallsExcludeInboundEscapedTo: boolean
    FilterUnreturnedLostCallsExcludeInboundDivertedOrOverflowedTo: boolean
    FilterUnreturnedLostCallsExcludeInboundTransferredTo: boolean
    FilterUnreturnedLostCallsExcludeAllOtherInbound: boolean
    IncludeOutboundCalls: boolean
    IncludeOutboundCallsFromDeviceList: string

    StatisticSettingACDNACode01: string
    StatisticSettingACDNACode02: string
    StatisticSettingACDNACode03: string
    StatisticSettingACDNACode04: string
    StatisticSettingACDNACode05: string
    StatisticSettingACDNACode06: string
    StatisticSettingACDNACode07: string
    StatisticSettingACDNACode08: string
    StatisticSettingACDNACode09: string
    StatisticSettingACDNACode10: string
    StatisticSettingACDNACode11: string

    StatisticSettingACDNACodeUseCodeDesc: boolean
    StatisticSettingSvcLvlAnsThres: number | undefined
    StatisticSettingSvcLvlAbndThres: number | undefined
    StatisticSettingSvcLvlIgnQckAbndCalls: boolean
    StatisticSettingSvcLvlQckAbndCallsThres: number | undefined
    StatisticSettingSvcLvlIgnOvrfldCalls: boolean
    StatisticSettingSvcLvlOvrfldCallsThres: number | undefined
    StatisticSettingUnreturnedLostCallsIncludeEndedOnHold: boolean
    StatisticSettingUnreturnedLostCallsIncludeEscaped: boolean
    StatisticSettingExcludeHeldTimeFromTalkTime: boolean
    StatisticSettingCallsInIgnQckAbndCalls: boolean
    StatisticSettingCallsInQckAbndCallsThres: number | undefined
    StatisticSettingCallsInIgnOvrfldCalls: boolean
    StatisticSettingCallsInOvrfldCallsThres: number | undefined
    StatisticSettingCallsInAnsThres1: number | undefined
    StatisticSettingCallsInAnsThres2: number | undefined
    StatisticSettingCallsInAnsThres3: number | undefined
    StatisticSettingCallsInAnsThres4: number | undefined
    StatisticSettingCallsInAnsThres5: number | undefined
    StatisticSettingCallsInAnsThres6: number | undefined
    StatisticSettingCallsInAbndThres1: number | undefined
    StatisticSettingCallsInAbndThres2: number | undefined
    StatisticSettingCallsInAbndThres3: number | undefined
    StatisticSettingCallsInAbndThres4: number | undefined
    StatisticSettingCallsInAbndThres5: number | undefined
    StatisticSettingCallsInAbndThres6: number | undefined
    StatisticSettingFinancialOrderValueInbound: string
    StatisticSettingFinancialOrderValueOutbound: string
    StatisticSettingFinancialStrikeRateInbound: number | undefined
    StatisticSettingFinancialStrikeRateOutbound: number | undefined
    StatisticSettingFinancialCurrencyID: number
    StatisticSettingFinancialCurrencyFormatPattern: string
    StatisticSettingFinancialCurrencyGroupingSeparator: string
    StatisticSettingFinancialCurrencyDecimalSeparator: string
    StatisticSettingMvdToUnResDevsAreOvrfldCalls: boolean
    DebugContentDump: boolean
    MaxRows: number | undefined
    MaxFilterEntities: number | undefined
    AlarmACDAvailableOn: boolean
    AlarmACDAvailableThres: number
    AlarmACDBusyOn: boolean
    AlarmACDBusyThres: number
    AlarmACDNADNDOn: boolean
    AlarmACDNADNDThres: number
    AlarmACDWrapUpOn: boolean
    AlarmACDWrapUpThres: number
    AlarmCallAlertOn: boolean
    AlarmCallAlertThres: number
    AlarmCallTalkOn: boolean
    AlarmCallTalkThres: number
    AlarmCallLostOn: boolean
    AlarmFirePlaySound: boolean
    AlarmFireActivateWin: boolean
    ZoomLevel: number
    ReportViewType: number
    ReportViewFlags: number
    PrintViewChartInclusion: number
    PrintViewFlags: number
    ChartProvider: number
    ChartType: number
    WallboardTileType: number
    IsAutoSwitched: boolean
    AutoSwitchIntervalDisplayText: string
    // AutoSwitchIntervalDisplayRealValue: number
    ExternalContentURL: string
    FormatCSVStatValuesType: number
    // GroupingViewType: number
    // UserLicensedFor: number
    // IsLicensed: boolean
    // IsLicensedRealTime: boolean
    // SupportsIconViews: boolean
    // SupportsCharting: boolean
    // SupportsRowHL: boolean
    // DurationFilterSummaryText: string
    schedule?: RptConfigurationDataUpdateScheduleDto
    StatisticSettingCallsInIgnEscapedCalls: boolean
}

export type RptConfigurationDataUpdateScheduleDto = {
    ScheduleDelete: boolean
    ScheduleReset: boolean
    ScheduleLocaleStr: string
    SchedulePaperSizeID: string
    ScheduleFormat: number
    ScheduleType: number
    ScheduleAtDate: string
    ScheduleAtDayOfWeek: number
    ScheduleAtDayOfWeekFilter: number
    ScheduleAtDayOfMonth: number
    // ScheduleAtMonthOfYear: number
    ScheduleAtTime: string
    ScheduleAtHourFilterStart: number
    ScheduleAtHourFilterEnd: number
    ScheduleAtMinute: number
    ScheduleAutoDuration: boolean
    ScheduleBundleAsZIP: boolean
    ScheduleMergeWithOtherSchedules: boolean
    // ScheduleStatusDateTimeStart: string
    // ScheduleStatusDateTimeNextPure: string
    // ScheduleStatusDateTimeNextActual: string
    // ScheduleStatusDateTimeLastPure: string
    // ScheduleStatusDateTimeLastActual: string
    // ScheduleStatusMessage: string
    // ScheduleStatusFailed: string
    MailingList: string
}

export type RptConfigurationDataResponseDto = {
    ID: string
    UserID: string
    UsersReportsScheduleID: string
    Description: string
    CustomIntervalValue: number
    CustomIntervalType: CallIntervalTypes
    FilterEntityType: number
    Information: string
    IsCustom: boolean
    CustomDefClassID: string
    CustomContentClassID: string
    Type: ReportTypes
    StatisticsContent: string
    StatisticsDisplay: string
    StatisticsSettings: string
    MethodsGroupedBy: string
    DateTimeBinned: string
    StatisticsOrderedBy: string
    StatisticsOrderedByAscOrDesc: string
    Duration: number
    DurationIsUTC: boolean
    DurationCustomStart: string
    DurationCustomEnd: string
    DurationDaysOfWeek: number
    DurationTimeOfDayStart: string // "00:00:00"
    DurationTimeOfDayEnd: string // "23:59:59"
    DurationDayCount: number
    FilterIDDb: string
    FilterIDTimeSync: string
    FilterCallIDThread: string
    FilterCallIDThreadWCMatches: boolean
    FilterCallIDTelSys: string
    FilterCallIDTelSysWCMatches: boolean
    FilterTelephoneSystemID: number
    FilterPartitionIDs: string
    FilterGroupIDs: string
    FilterTelNos: string
    FilterTelNoDescriptions: string
    FilterDevices: string
    FilterDeviceTypes: string
    FilterDevicesIfGrpIncGrpMmbrCalls: boolean
    FilterDevicesIfGrpIncACDItemsForGrpDevOnly: boolean
    FilterACDAgents: string
    FilterLocation: number
    FilterCallType: number
    FilterCallTypeConfCallAsExternal: boolean
    FilterCallChannels: number
    FilterSubject: string
    FilterAnsType: number
    FilterCLIType: number
    FilterDDIDigits: string
    FilterAccountCodes: string
    FilterMovedToTelNos: string
    FilterMovedFromDevices: string
    FilterMovedFromACDAgents: string
    FilterMovedSegNo: number
    FilterMovedSegReasonStarted: number
    FilterMovedSegReasonEnded: number
    FilterCallTimeRngAllCmp: number
    FilterCallTimeRngAllValue1: number
    FilterCallTimeRngAllValue2: number
    FilterCallTimeRngSegCmp: number
    FilterCallTimeRngSegValue1: number
    FilterCallTimeRngSegValue2: number
    FilterCallTimeTalkCmp: number
    FilterCallTimeTalkValue1: number
    FilterCallTimeTalkValue2: number
    FilterCallTimeAllCmp: number
    FilterCallTimeAllValue1: number
    FilterCallTimeAllValue2: number
    FilterCallTimeHeldCmp: number
    FilterCallTimeHeldValue1: number
    FilterCallTimeHeldValue2: number
    FilterUnreturnedLostCallsExcludeInboundEscapedTo: boolean
    FilterUnreturnedLostCallsExcludeInboundDivertedOrOverflowedTo: boolean
    FilterUnreturnedLostCallsExcludeInboundTransferredTo: boolean
    FilterUnreturnedLostCallsExcludeAllOtherInbound: boolean
    IncludeOutboundCalls: boolean
    IncludeOutboundCallsFromDeviceList: string
    StatisticSettingACDNACode01: string
    StatisticSettingACDNACode02: string
    StatisticSettingACDNACode03: string
    StatisticSettingACDNACode04: string
    StatisticSettingACDNACode05: string
    StatisticSettingACDNACode06: string
    StatisticSettingACDNACode07: string
    StatisticSettingACDNACode08: string
    StatisticSettingACDNACode09: string
    StatisticSettingACDNACode10: string
    StatisticSettingACDNACode11: string
    StatisticSettingACDNACodeUseCodeDesc: boolean
    StatisticSettingSvcLvlAnsThres: number
    StatisticSettingSvcLvlAbndThres: number
    StatisticSettingSvcLvlIgnQckAbndCalls: boolean
    StatisticSettingSvcLvlQckAbndCallsThres: number
    StatisticSettingSvcLvlIgnOvrfldCalls: boolean
    StatisticSettingSvcLvlOvrfldCallsThres: number
    StatisticSettingUnreturnedLostCallsIncludeEndedOnHold: boolean
    StatisticSettingUnreturnedLostCallsIncludeEscaped: boolean
    StatisticSettingExcludeHeldTimeFromTalkTime: boolean
    StatisticSettingCallsInIgnQckAbndCalls: boolean
    StatisticSettingCallsInQckAbndCallsThres: number
    StatisticSettingCallsInIgnOvrfldCalls: boolean
    StatisticSettingCallsInOvrfldCallsThres: number
    StatisticSettingCallsInAbndThres1: number
    StatisticSettingCallsInAbndThres2: number
    StatisticSettingCallsInAbndThres3: number
    StatisticSettingCallsInAbndThres4: number
    StatisticSettingCallsInAbndThres5: number
    StatisticSettingCallsInAbndThres6: number
    StatisticSettingCallsInAnsThres1: number
    StatisticSettingCallsInAnsThres2: number
    StatisticSettingCallsInAnsThres3: number
    StatisticSettingCallsInAnsThres4: number
    StatisticSettingCallsInAnsThres5: number
    StatisticSettingCallsInAnsThres6: number
    StatisticSettingFinancialOrderValueInbound: number
    StatisticSettingFinancialOrderValueOutbound: number
    StatisticSettingFinancialStrikeRateInbound: number
    StatisticSettingFinancialStrikeRateOutbound: number
    StatisticSettingFinancialCurrencyID: number
    StatisticSettingFinancialCurrencyFormatPattern: string
    StatisticSettingFinancialCurrencyGroupingSeparator: number
    StatisticSettingFinancialCurrencyDecimalSeparator: number
    StatisticSettingMvdToUnResDevsAreOvrfldCalls: boolean
    StatisticSettingCallsInIgnEscapedCalls: boolean
    DebugContentDump: boolean
    MaxRows: number
    MaxFilterEntities: number
    AlarmACDAvailableOn: boolean
    AlarmACDAvailableThres: number
    AlarmACDBusyOn: boolean
    AlarmACDBusyThres: number
    AlarmACDNADNDOn: boolean
    AlarmACDNADNDThres: number
    AlarmACDWrapUpOn: boolean
    AlarmACDWrapUpThres: number
    AlarmCallAlertOn: boolean
    AlarmCallAlertThres: number
    AlarmCallTalkOn: boolean
    AlarmCallTalkThres: number
    AlarmCallLostOn: boolean
    AlarmFirePlaySound: boolean
    AlarmFireActivateWin: boolean
    ZoomLevel: number
    ReportViewType: number
    ReportViewFlags: number
    PrintViewChartInclusion: number
    PrintViewFlags: number
    ChartProvider: number
    ChartType: number
    WallboardTileType: number
    IsAutoSwitched: boolean
    AutoSwitchIntervalDisplay: number
    ExternalContentURL: string
    FormatCSVStatValuesType: number
    GroupingViewType: number
    UserLicensedFor: number
    IsLicensed: boolean
    IsLicensedRealTime: boolean
    SupportsIconViews: boolean
    SupportsCharting: boolean
    SupportsRowHL: boolean
    DurationFilterSummaryText: string
    Schedule?: RptConfigurationDataSchedule
}

export type RptConfigurationDataSchedule = {
    ID: string // 'b7e4ac22c9a78b40:-2aefbfcc:174489fc681:-7fa3'
    IsActive: boolean
    UsersReportID: string // '289fad24703011ca:75434d35:173a17def0b:-7fd9'
    LocaleStr: string // 'en_GB'
    PaperSizeID: string // 'A4'
    Format: number // 10
    StatusDateTimeStart: string // '1 Sep 2020, 07:51:51'
    StatusDateTimeLastPure: string // '1 Sep 2020, 21:00:00'
    StatusDateTimeLastActual: string // '1 Sep 2020, 21:16:58'
    StatusMessage: string // 'Last schedule completed successfully and should be e-mailed to service@akixi.com, once the e-mail dispatch controller subsequently becomes available from performing other messaging requests.'
    StatusFailed: boolean
    Type: number // 20,
    AtDate: string // "",
    AtDayOfWeek: number
    AtDayOfWeekFilter: number
    AtDayOfMonth: number
    AtMonthOfYear: number
    AtTime: string // "23:00:00",
    AtHourFilterStart: number
    AtHourFilterEnd: number
    AtMinute: number
    AutoDuration: boolean
    BundleAsZIP: boolean
    MergeWithOtherSchedules: boolean
    UserUniqueID: string // "d7ae7ea9bf56ebdc:7efd8c37:132837798cb:-7ff7",
    RunningNow: boolean
    StatusDateTimeNext: string // "2 Sep 2020, 21:00:00",
    DateTimeNextUpdateForceToggle: boolean
    MailingList: string
}

export interface Device {
    DeviceNumber: string
    Description: string
    DeviceTypeID?: number
    Users?: string[]
    TelSysUsername?: string
    TelSysUserID?: string
    IDOnTelSys?: string
    ACDAgentNumber?: string
    OverrideMonitoring?: boolean
    LicenseType?: string
    QueueStatus?: string
    DisableRTMonitoring?: boolean
    QueueCompatibility?: boolean
    IDOnTelSysOwner?: string
    CallQueueCaptureErrorMessage?: string
    PrimaryEmail?: string
}

export interface Agent {
    AgentNumber: string
    Description: string
    TelSysUsername?: string
    IDOnTelSys?: string
    OverrideMonitoring?: boolean
}

export interface AccountCode {
    Code: string
    Description: string
}

export type NACode = {
    Code: string
    Description: string
}

export type Group = {
    ID: string
    Description: string
}

export type CME = {
    Description: string
    CMEnvironmentID: string
    Capabilities?: number
}

export type TelSys = {
    Description: string
    TelSysID: number
    TelSysTypeID: number
    TelSysType?: TelSysTypes
    PartitionLicences?: string[]
}

export type Partition = {
    Description: string
    PartIDInTelSys: number
    LicensedForRptUserType: string
}

export enum TelSysIntegrationMode {
    REAL_TIME = 'CXA Real-time + CXI',
    HISTORIC = 'CXA Historical + CXI',
    CX_ANALYTICS = 'CX Analytics + CXI',
    CXI_ONLY = 'CXI Only',
    CX_AUDIT = 'CX Audit Only',
    PROFESSIONAL_REAL_TIME = 'Professional - Real-Time',
    PROFESSIONAL_HISTORIC = 'Professional - Historic'
}

export type IndividualTelItem = {
    TelSysID: number
    SyncNow: boolean
    TimeZone: string
    Advanced: {
        CTILogIncClassNames: boolean
        InternalDiagDumpEnabled: boolean
        CallModelDiagDumpEnabledForCalls: boolean
        CTILogEnabled: boolean
        PartitionsRptingScopeAllEnabled: boolean
        PartitionCountMax: number
        CTICallModelLogEnabled: boolean
        MaximumAllowedUsersMonitored?: number
    }
    Description: string
    CommsEnabled: boolean
    MonitoringEnabled?: boolean
    TelNoPrefixes: number
    TelSysType: TelSysTypes
    LicensedForRptUserType?: string
    CMEnvironmentID: string
    TCPPort1: number
    ManageUserMFA?: boolean
    PartitionLicences?: string[]
    HistoricOnly: boolean
    IntegrationMode?: TelSysIntegrationMode
    IntegrationType?: IntegrationType
    IntegrationModeOptions?: Nullable<string>
    CallControlOptions?: string
    BotsDisabled?: boolean
    AuthUserMissingRoles?: string[]
    ManualRTQueuesEnabled: boolean
    CallRecIntIDDefault?: string
}

export type DefaultReportItem = {
    ID: string
    Weight: number
    SelectedByDefault: boolean
}

export type messageType = string | ReactNode

export type GenericDeviceCollections = {
    Items?: Device[]
    Total?: number
    Devices?: Device[]
    TotalDevices?: number
    TotalACDAgents?: number
}

export type GenericAgentsCollections = {
    Items?: Agent[]
    ACDAgents?: Agent[]
}

export type SignInBody = {
    Device: string
    ACDGroup?: string
}

export type SignInOptions = {
    agentIDInput?: string
    deviceIDInput?: string
    groupInput?: string | string[]
}

export type StatusAPIData = {
    ACDGroupsSignedIn?: [
        {
            ID: string
        }
    ]
    ACDStatus?: string
    ACDStatusReasonTxtID?: string
    DeviceState?: string
    ErrorCode?: number
    ErrorMessage?: string
}

export type ReactState<T> = [T, StateSetter<T>]
export type ReactStateAsync<T> = [T, StateSetter<T>, () => Promise<T>]
export type ReactStatePromise<T> = [T, StateSetterPromise<T>]
export type StateSetterPromise<T> = (value: T | ((prevState: T) => T)) => Promise<T>
export type StateSetter<T> = React.Dispatch<StateSetterAction<T>>
export type StateSetterAction<T> = React.SetStateAction<T>

export type EditableProps = {
    isEditing: boolean
    setIsEditing: StateSetter<boolean>
    editingComponent: ReactElement
    editableHintText: string
    editablePlaceholder: string
    isEditingElsewhere: boolean
}

export type FieldObj = {
    field: string
    value: string | number
    editableProps?: EditableProps
}

export type CollapseObj = {
    title: string
    isOpen: boolean
    toggle: () => void
    data: FieldObj[]
}

export type AllRecordingData = {
    Description: string
    Duration: string
    From: string
    ID: string
    Number: string
    TimeStart: string
    To: string
}

export type RecordingData = {
    ID: string
    Number: string
    Description: string
    From: string
    To: number
    TimeStart: string
    Duration: number
    RecordingURL: string
    RecordingURLValidFor: number
    RecordingURLFileName: string
    RecordingURLCapabilities: number
}

export type DirectoryEntry = {
    TelephoneNumber: string
    Description: string
}

export interface MenuOption {
    title?: string
    loading?: boolean
    isVisible?: boolean
    isEnabled?: boolean
    checked?: boolean
    tooltip?: string
    renderedIcon?: ReactElement
    icon?: string
    submenu?: MenuOption[]
    renderedSubMenu?: ReactElement
    onClick?: (e: React.MouseEvent<Element, MouseEvent>) => Promise<void | Response> | void
    onHover?: () => Promise<void> | void
}

export type MenuItemStatus = {
    displayed: boolean
    enabled: boolean
}

export class RowOptionsEliminator {
    basic: boolean = true
    monitor: boolean = true
    signInOut: boolean = true
    status: boolean = true
    accountCode: boolean = true
    advanced: boolean = true
}

export type CallDetailsRowData = {
    telSysID: string
    partitionID: string
    callDetails: CallDetailsInfo
}

export type CallDestination = {
    TelSysID?: string
    PartitionIDInTelSys?: string
    Destination: string
    DestinationAutoPrefix?: boolean
    DeviceIsParkLocation?: boolean
}

export type CallPickupLocation = {
    TelSysID?: string
    PartitionIDInTelSys?: string
    DeviceID: string
    DeviceIsParkLocation?: boolean
}

export type CallCtrlProps = {
    Flags: Nullable<number>
    TelSysID: Nullable<string>
    PartitionID: Nullable<string>
    ACDAgentID: Nullable<string>
    DeviceID: Nullable<string>
}

export type ReportStat = {
    Alarming: boolean
    FormattedValue: string
    ID: number
    LongFormattedValue: string
    LongFormattedValueNoOffset: string
    LongFormattedMillisSinceDateTime: string
    RawValue: Nullable<string | number | boolean>
}

export type Definition = {
    AlarmThresholds: {
        Type: string
        Minimum: Nullable<number>
        Maximum: Nullable<number>
    }
    BackgroundColour: string
    Colour: string
    DataType: string
    Format: string
    ID: number
    IsTitleDisplayed: boolean
    LongDescription: string
    LongFormat: string
    MediumDescription: string
    ShortDescription: string
}

export type FieldDefinition = {
    Definition: Definition
}

export type ReportRow = {
    Alarming: boolean
    CallCtrlProperties: CallCtrlProps
    Description: string
    HighlightStatus: string
    RowID: string
    Statistics: ReportStat[]
}

export type ReportRowWrapper = {
    Row: ReportRow
}

export type Report = {
    Alarming: boolean
    Body: {
        Rows: ReportRowWrapper[]
    }
    CallCtrlProperties: CallCtrlProps
    Description: string
    Duration: string
    ExecutionStatus: string
    Footer: Nullable<ReportRowWrapper[]>
    GroupingViewType: string
    Header: {
        SortedBy: string[]
        FieldDefinitions: FieldDefinition[]
    }
    IsIncrementalUpdate: boolean
    Metadata: {
        HasPreviousItem: boolean
        HasNextItem: boolean
        ItemFilterStart: string
        ItemFilterEnd: string
    }
    NewlyAlarming: boolean
    ReportID: string
    ReportType: number
    ServerTimeZone: string
    UserTimeZone: string
}

export type CallDetailsWrapperResponse = {
    CallSegments: {
        Reports: Report[]
    }
    CallJourney: CallJourney
}

export type CallStats = {
    CallId: string
    DeviceCalled: string
    DeviceCalling: string
    IsAnswered: boolean
    TotalCallTime: string
}

export type CallJourney = {
    Timeline: CallJourneyTimelineItem[]
} & CallStats

export type CallJourneyAction =
    | 'Started (Inbound)'
    | 'Started (Outbound)'
    | 'Started'
    | 'Forwarded'
    | 'Transferred'
    | 'Moved'
    | 'Pickup'
    | 'Transfer Merge'
    | 'HuntGroup Advance'
    | 'Conferenced'
    | 'Answered'
    | 'Abandoned'
    | 'Ended'
    | 'Ended on Hold'
    | 'Transfer Merge Consultation Answered'
    | 'Diverted'

export type CallJourneyTimelineItem = {
    Action: CallJourneyAction
    Description: string
    Duration: string
    Time: string
}

export type ReportWrapperResponse = {
    Reports: Report[]
}

export type ParsedReportStatsByID = {
    [id: number]: ParsedReportStat
}

export type ParsedReportStat = {
    fieldName: string
    fieldValue: string
    longFormattedValueNoOffset: string
    longFormattedMillisSinceDateTime: string
    rawValue: Nullable<string | number | boolean>
}

export type MediaPlayerRef = React.MutableRefObject<{
    audio?: HTMLAudioElement
    handlePlay?: () => void
    handlePause?: () => void
    playing?: boolean
}>

// eslint-disable-next-line
export type ReactRef = React.Ref<any>

export type RequestMapperOptions<U extends ServerError> = {
    errorMessageMapper?: (errors: U[]) => { serverError: string; successfulCodes?: string }
    overrideStatusCode?: number
}

export type CacheResponse<T extends unknown> = {
    res: JSONResponse<T>
    subscription?: Subscription
}

export type BaseEvent = {
    clientX: number
    clientY: number
}

export type DashboardSubReportEntry = {
    element: Element
    id: string
    index: number
    mainReportID: string
    container: Element
}

export type Rules =
    | Partial<{
          required: string | boolean | ValidationValueMessage<boolean>
          min: ValidationRule<React.ReactText>
          max: ValidationRule<React.ReactText>
          maxLength: ValidationRule<React.ReactText>
          minLength: ValidationRule<React.ReactText>
          pattern: ValidationRule<RegExp>
          validate: Validate | Record<string, Validate>
          valueAsNumber: boolean
          valueAsDate: boolean
          // eslint-disable-next-line
          setValueAs: (value: any) => any
      }>
    | undefined

export type CallRecordingIntegration = {
    ID: string
    Description: string
    Type: number
}

export type LanguageDefinition = {
    Value: string
    Key: string
}

export type ResourceBundle = {
    Locale: string
    Description: string
}

export type ServerPasswordConfig = {
    UserPasswordMinEntropyAdmin: number
    UserPasswordEmptyAllowAdmin: boolean
    UserPasswordEmptyAllowStd: boolean
    UserPasswordMinLenStd: number
    UserPasswordDefaultLen: number
    UserPasswordMinLenAdmin: number
    UserPasswordMinEntropyStd: number
}

export type ServerResourceBundles = ResourceBundle[]

export type PasswordConfig = {
    admin: PasswordInputConfig
    standard: PasswordInputConfig
}

export type LanguageConfig = SelectOptions<string>

export type UserConfigEntries = [PasswordConfig, LanguageConfig]

export type RoleOption = {
    label: string
    value: boolean
}

export type BasicContextProps = {
    children: ReactNode
}

export type ServerErrorOptions = {
    className: string
    icon: { name: IconName; size: IconSize }
    headingLevel: HeadingLevel
    title: string
    message: string
}

export type StandardAsyncRESTOptions = StandardRESTOptions & {
    timingOptions?: TimingOptions
}

export interface AsyncGETRESTOptions extends StandardAsyncRESTOptions {
    method: RESTMethods.GET
    body?: undefined
}

export interface AsyncModifyRESTOptions extends StandardAsyncRESTOptions {
    method: RESTMethods.POST | RESTMethods.PATCH | RESTMethods.PUT | RESTMethods.DELETE
    body?: object
}

export type AsyncRESTOptions = AsyncGETRESTOptions | AsyncModifyRESTOptions

export type LicenceValue = {
    Value: string
    Usage: string
    Description: string
    Name: string
    StatusMessage?: string
}

export type LicenceValues = Record<string, string>

export type TelSysProfile<T extends unknown> = {
    ID: string
    LicenseValues: T
    Name: string
    type?: string
}

export type ProcessedTelSysProfile = TelSysProfile<LicenceValues>

export interface AppLicenceDataList extends APIListData<LicenceValue> {
    Data: {
        Items: LicenceValue[]
        TelSysProfiles: TelSysProfile<LicenceValue[]>[]
    }
}

export enum BgType {
    Image = 'IMAGE',
    Video = 'VIDEO'
}

export type LocaleEntry = {
    description: string
    locale: string
}

export type LocaleEntries = LocaleEntry[]

export type RecaptchaResponseNotEnabled = {
    enabled: false
    captchaType?: undefined
    publicKey?: undefined
}

export type ReCaptchaResponseEnabled = {
    enabled: true
    captchaType: string
    publicKey: string
}

export type ReCaptchaResponse = ReCaptchaResponseEnabled | RecaptchaResponseNotEnabled

export type DoctopicWindow = Window & typeof globalThis & { modalTopicID: string | undefined }

export type UserAgentDTO = {
    userAgentID: number
}

export type BrandingConfigByTypeResponse = {
    BrandingGUID: string
}

export type ReportEntityTypes = {
    id: number
    description: string
    reportTypeDefId: ReportTypes
}

export type MessageResponseTuple = ['warning' | 'error', string]

export type GroupMembershipResponse = {
    ACDAgents: AgentResponse[]
    Devices: DeviceResponse[]
}

export type UserCompliancePolicy = {
    policyType: string
    policyName: string
    assignmentType?: string
    reference?: Nullable<string>
    policySource?: UserCompliancePolicy[]
}

export type ComplianceRecordingApplication = {
    id: string
    complianceRecordingPairedApplications: {
        id: string
        type: string
        applicationInstanceId: string
    }[]
    requiredBeforeMeetingJoin: boolean
    requiredBeforeCallEstablishment: boolean
    requiredDuringMeeting: boolean
    requiredDuringCall: boolean
    concurrentInvitationCount: number
}

export type TenantCompliancePolicy = {
    type: string
    enabled: boolean
    customBanner: Nullable<string>
    customPromptsEnabled: boolean
    customPromptsPackageId: Nullable<string>
    complianceRecordingApplications: Nullable<ComplianceRecordingApplication[]>
    disableComplianceRecordingAudioNotificationForCalls: boolean
    recordReroutedCalls: boolean
    warnUserOnRemoval: boolean
    identity: string
    description: Nullable<string>
}

export type UserCompliancePoliciesWrapper = {
    userCompliancePolicies: Nullable<UserCompliancePolicy[]>
    tenantCompliancePolicies: Nullable<TenantCompliancePolicy[]>
}

export type UsersCompliancePoliciesWrapper = {
    usersCompliancePolicies: Nullable<Record<string, UserCompliancePolicy[]>>
    tenantCompliancePolicies: Nullable<TenantCompliancePolicy[]>
}

export type PresenceStatus = {
    name: string
    department: Nullable<string>
    enabledCapabilities: string[]
    captureEnabled: boolean
    captureInEffect: boolean
    presenceCaptureEnabled: boolean
    knownGood: boolean
    userType: string
    id: string
    userPrincipalName: string
    lineUri: Nullable<string>
}

export type PolicyAssignment = { PolicyId: string; PolicyName: string }
